/**
 * Hook: useTeamMessages
 *
 * Manages all state and side-effects for the Koredor internal team messaging
 * feature. Wraps the team-message service layer and wires a Realtime
 * subscription for incoming messages on the active conversation.
 *
 * Realtime pattern follows src/hooks/useJobDetailQuery.ts.
 * No Supabase calls are made here — everything goes through
 * src/services/team-message.service.ts.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getMyConversations,
  getMessages,
  sendMessage,
  markMessagesRead,
  getTotalUnreadCount,
  getUnreadCountsPerConversation,
  getLastMessagesPerConversation,
  getOrgMembers,
  createGroupConversation,
  createChannel,
  findOrCreateDM,
  addMembersToConversation,
  findOrCreateGeneralChannel,
  findOrCreateUpdatesChannel,
  insertMentions,
  getUnreadMentionCount,
  markMentionsRead,
} from '@/services/team-message.service';
import type {
  TeamConversation,
  TeamMessage,
  TeamMessageAttachment,
  OrgMember,
} from '@/types/teamMessages';

// =============================================================================
// Hook
// =============================================================================

export function useTeamMessages() {
  const [conversations, setConversations] = useState<TeamConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mentionCount, setMentionCount] = useState(0);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  // Derived — always in sync with the loaded conversations list, no UUID mismatch
  const updatesChannelId = conversations.find((c) => c.type === 'updates')?.id ?? null;

  // Realtime channel for mention notifications
  const mentionRealtimeRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Keep a ref to the active realtime channel so we can clean it up on
  // conversation change without re-declaring the subscription effect.
  const realtimeRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Global subscription — watches ALL team_messages inserts to keep conversation
  // list previews (lastMessage, updatedAt, unreadCount) live for every conversation.
  const globalRealtimeRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const activeConvIdRef   = useRef<string | null>(null);

  // Keep ref in sync so global realtime handler always reads current active conv
  useEffect(() => { activeConvIdRef.current = activeConversationId; }, [activeConversationId]);

  // ---------------------------------------------------------------------------
  // Data loaders
  // ---------------------------------------------------------------------------

  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const data = await getMyConversations();
      const convIds = data.map((c) => c.id);
      // Fetch unread counts AND last message previews in parallel
      const [unreadMap, lastMsgMap] = await Promise.all([
        getUnreadCountsPerConversation(convIds),
        getLastMessagesPerConversation(convIds),
      ]);
      setConversations(data.map((c) => ({
        ...c,
        unreadCount: unreadMap.get(c.id) ?? 0,
        lastMessage: lastMsgMap.get(c.id) ?? null,
      })));
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    setLoadingMessages(true);
    try {
      const data = await getMessages(conversationId);
      setMessages(data);
      // Mark messages + mentions as read, refresh global badges
      await markMessagesRead(conversationId);
      void markMentionsRead(conversationId);
      const [count, mCount] = await Promise.all([
        getTotalUnreadCount(),
        getUnreadMentionCount(),
      ]);
      setUnreadCount(count);
      setMentionCount(mCount);
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)),
      );
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Actions exposed to the UI
  // ---------------------------------------------------------------------------

  /** Select a conversation and load its messages. */
  const selectConversation = useCallback(
    (id: string) => {
      setActiveConversationId(id);
      void loadMessages(id);
    },
    [loadMessages],
  );

  /**
   * Send a message in the active conversation.
   * Appends optimistically to local state so the UI feels instant.
   * mentionedUserIds: auth.users.id values for every @mentioned user in the body.
   */
  const handleSend = useCallback(
    async (
      body: string,
      attachments: TeamMessageAttachment[] = [],
      mentionedUserIds: string[] = [],
    ) => {
      if (activeConversationId === updatesChannelId) return  // read-only
      if (!activeConversationId) return;

      const tempId = `optimistic-${Date.now()}`;
      const now = new Date().toISOString();

      // Optimistic: show message immediately with a temp id
      const optimistic: TeamMessage = {
        id: tempId,
        conversationId: activeConversationId,
        senderId: 'me',
        senderName: 'Me',
        senderInitials: 'ME',
        body,
        attachments,
        createdAt: now,
        isOwn: true,
      };
      setMessages((prev) => [...prev, optimistic]);

      try {
        const persisted = await sendMessage(activeConversationId, body, attachments);
        // Replace optimistic entry with server-confirmed record
        setMessages((prev) => prev.map((m) => m.id === tempId ? persisted : m));
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConversationId
              ? { ...c, lastMessage: persisted, updatedAt: persisted.createdAt }
              : c,
          ),
        );
        // Fire-and-forget mention rows — non-critical
        if (mentionedUserIds.length) {
          void insertMentions(persisted.id, activeConversationId, mentionedUserIds);
        }
      } catch {
        // Roll back on failure
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    },
    [activeConversationId, updatesChannelId],
  );

  /** Create a new named group conversation. */
  const handleCreateGroup = useCallback(
    async (name: string, memberIds: string[]) => {
      const conv = await createGroupConversation(name, memberIds);
      // Reload to get the fully hydrated record with members, then open it
      await loadConversations();
      setActiveConversationId(conv.id);
      void loadMessages(conv.id);
    },
    [loadConversations, loadMessages],
  );

  /** Create a new channel (org-wide named room). */
  const handleCreateChannel = useCallback(
    async (name: string) => {
      await createChannel(name);
      await loadConversations();
    },
    [loadConversations],
  );

  /** Open a DM with another user, creating one if it does not exist. */
  const handleStartDM = useCallback(
    async (userId: string) => {
      const conv = await findOrCreateDM(userId);
      await loadConversations();
      setActiveConversationId(conv.id);
      void loadMessages(conv.id);
    },
    [loadConversations, loadMessages],
  );

  /**
   * Adds members to an existing group conversation.
   * On success, appends the newly added members to the active conversation's
   * members array in local state — avoids a full refetch.
   */
  const handleAddMembers = useCallback(
    async (conversationId: string, memberIds: string[]) => {
      await addMembersToConversation(conversationId, memberIds);

      // Update local state: append new members from orgMembers that match the added IDs
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== conversationId) return conv;
          const newMembers = orgMembers.filter(
            (m) =>
              memberIds.includes(m.userId) &&
              !conv.members.some((existing) => existing.userId === m.userId),
          );
          return {
            ...conv,
            members: [
              ...conv.members,
              ...newMembers.map((m) => ({
                userId: m.userId,
                displayName: m.displayName,
                initials: m.initials,
                role: m.role,
                joinedAt: new Date().toISOString(),
              })),
            ],
          };
        }),
      );
    },
    [orgMembers],
  );

  // ---------------------------------------------------------------------------
  // Realtime subscription — active conversation messages
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!activeConversationId) return;

    // Unsubscribe from any previous channel before opening a new one
    realtimeRef.current?.unsubscribe();

    // Suffix channel name with Date.now() to prevent React StrictMode
    // double-mount from colliding on the same channel name.
    realtimeRef.current = supabase
      .channel(`team_messages:${activeConversationId}:${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_messages',
          filter: `conversation_id=eq.${activeConversationId}`,
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // Own messages are already in state via optimistic update in handleSend
          if (payload.new.sender_id === user.id) return;

          const isSystem = payload.new.is_system === true;
          let senderName = 'System';
          let senderInitials = 'SY';

          // Skip profile lookup for system messages (sender_id is null)
          if (!isSystem && payload.new.sender_id) {
            let profile: { full_name?: string; email?: string } | null = null;
            const { data: byAuthId } = await supabase
              .from('profiles')
              .select('full_name, email')
              .eq('auth_user_id', payload.new.sender_id)
              .maybeSingle();
            if (byAuthId) {
              profile = byAuthId;
            } else {
              const { data: byId } = await supabase
                .from('profiles')
                .select('full_name, email')
                .eq('id', payload.new.sender_id)
                .maybeSingle();
              profile = byId;
            }
            senderName = String(profile?.full_name ?? profile?.email ?? 'Unknown');
            senderInitials = senderName
              .split(/\s+/)
              .filter(Boolean)
              .map((w: string) => w[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);
          }

          const msg: TeamMessage = {
            id: String(payload.new.id),
            conversationId: String(payload.new.conversation_id),
            senderId: String(payload.new.sender_id ?? ''),
            senderName,
            senderInitials,
            body: String(payload.new.body ?? ''),
            attachments: Array.isArray(payload.new.attachments)
              ? (payload.new.attachments as TeamMessageAttachment[])
              : [],
            createdAt: String(payload.new.created_at),
            isOwn: false,
            isSystem,
            metadata: payload.new.metadata ?? undefined,
          };

          setMessages((prev) => [...prev, msg]);

          // Bump unread count for new messages arriving in inactive conversations
          // (this conversation is active so we mark it read immediately)
          await markMessagesRead(String(payload.new.conversation_id));
          const count = await getTotalUnreadCount();
          setUnreadCount(count);
        },
      )
      .subscribe();

    return () => {
      realtimeRef.current?.unsubscribe();
    };
  }, [activeConversationId]);

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    void loadConversations();
    void getOrgMembers().then(setOrgMembers);
    void getTotalUnreadCount().then(setUnreadCount);
    void getUnreadMentionCount().then(setMentionCount);
    void supabase.auth.getUser().then(({ data }) => {
      if (data.user) setCurrentUserId(data.user.id);
    });

    // Ensure both system channels exist, then reload conversation list
    void Promise.all([
      findOrCreateGeneralChannel().catch(console.warn),
      findOrCreateUpdatesChannel().catch(console.warn),
    ]).then(() => {
      void loadConversations();
    });

    // ── Global realtime: keep conversation list previews live ─────────────────
    // Watches ALL team_messages inserts (RLS scopes to org). Updates lastMessage
    // and updatedAt on the matching conversation, and increments unreadCount for
    // conversations that are not currently open.
    globalRealtimeRef.current = supabase
      .channel(`team_messages_global:${Date.now()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'team_messages' },
        async (payload) => {
          const convId = String(payload.new.conversation_id);
          const createdAt = String(payload.new.created_at);
          const body = String(payload.new.body ?? '');
          const isSystem = payload.new.is_system === true;
          const senderId = payload.new.sender_id ? String(payload.new.sender_id) : null;

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const isOwn = senderId === user.id;
          const isActiveConv = activeConvIdRef.current === convId;

          // Build a minimal lastMessage preview
          const preview: TeamMessage = {
            id: String(payload.new.id),
            conversationId: convId,
            senderId: senderId ?? '',
            senderName: isSystem ? 'System' : isOwn ? 'You' : '',
            senderInitials: isSystem ? 'SY' : isOwn ? 'ME' : '',
            body,
            attachments: [],
            createdAt,
            isOwn,
            isSystem,
          };

          // Update the conversation's preview and bump unread if not active
          setConversations((prev) =>
            prev.map((c) => {
              if (c.id !== convId) return c;
              const shouldIncrementUnread = !isActiveConv && !isOwn && !isSystem;
              return {
                ...c,
                lastMessage: preview,
                updatedAt: createdAt,
                unreadCount: shouldIncrementUnread ? (c.unreadCount ?? 0) + 1 : c.unreadCount,
              };
            }),
          );

          // Refresh global unread badge if this is not the active conversation
          if (!isActiveConv && !isOwn) {
            const count = await getTotalUnreadCount();
            setUnreadCount(count);
          }
        },
      )
      .subscribe();

    // ── Mention realtime: increment badge + toast when current user is tagged ──
    // Imports toast lazily to avoid circular deps with sonner at module level.
    mentionRealtimeRef.current = supabase
      .channel(`team_message_mentions:${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_message_mentions',
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          // RLS guarantees only the mentioned user's rows arrive here, but double-check
          if (payload.new.mentioned_user_id !== user.id) return;

          setMentionCount((prev) => prev + 1);

          // Fire a toast to notify the user
          const { toast } = await import('sonner');
          toast('You were mentioned', {
            description: 'Someone mentioned you in a conversation.',
            duration: 5000,
          });
        },
      )
      .subscribe();

    return () => {
      globalRealtimeRef.current?.unsubscribe();
      mentionRealtimeRef.current?.unsubscribe();
    };
  }, [loadConversations]);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    conversations,
    activeConversationId,
    messages,
    orgMembers,
    unreadCount,
    mentionCount,
    loadingConversations,
    loadingMessages,
    updatesChannelId,
    currentUserId,
    selectConversation,
    handleSend,
    handleCreateGroup,
    handleCreateChannel,
    handleStartDM,
    handleAddMembers,
    reload: loadConversations,
  };
}
